/**
 * Guía: Cuentas Bancarias.
 *
 * Contenido derivado del capítulo **23** del manual: §0 (qué se decide aquí y
 * por qué aprobar no es un trámite), §1 (los tres alcances, y que el de
 * asesor sale de su cartera) y §2 (el ciclo y sus tres garantías: el OTP al
 * registrar, que nace sin verificar, y que aprobar y rechazar se auditan y
 * se notifican).
 *
 * El paso que más importa es el segundo: aprobar es lo que enciende la
 * bandera que el motor de liquidación exige para transferir. Sin esa
 * lectura, la pantalla parece una bandeja de papeleo.
 */

import type { TourFlow } from '../types';

const CUENTAS_PATH = '/finance-bank-accounts';

export const c23CuentasBancarias: TourFlow = {
  id: 'admin.finance-bank-accounts',
  chapter: '23',
  title: 'Cuentas bancarias',
  summary:
    'A dónde va el dinero de un comercio: por qué aprobar no es un trámite y qué cambia con cada decisión.',
  href: CUENTAS_PATH,
  requiresCountry: true,
  estimatedMin: 3,
  steps: [
    {
      id: 'intro',
      title: 'Aquí se decide a dónde va el dinero',
      body:
        'El comercio registra su cuenta desde su propio panel y aquí alguien la aprueba o ' +
        'la rechaza. Es una bandeja de revisión, y la revisión es el control: el motor de ' +
        'liquidación **sólo transfiere a cuentas aprobadas**.',
      route: CUENTAS_PATH,
    },
    {
      id: 'why-approval-matters',
      title: 'Aprobar no es papeleo',
      body:
        'Existe para que alguien con permisos de finanzas no pueda registrar su propia ' +
        'cuenta y cobrar. Por eso la cuenta nace sin verificar y sólo la aprobación la ' +
        'habilita. Aprobar sin mirar el certificado bancario vacía el control entero.',
    },
    {
      id: 'tabs',
      title: 'La pestaña que importa es «pendientes»',
      body:
        'Es la cola de trabajo: cuentas registradas esperando decisión. Mientras estén ahí, ' +
        'ese comercio no cobra — así que una cola larga son comercios sin liquidar.',
      target: 'admin.bankAccounts.tabs',
      placement: 'bottom',
      optionalTarget: true,
    },
    {
      id: 'what-you-see',
      title: 'Qué se comprueba en cada tarjeta',
      body:
        'Banco, tipo de cuenta, titular y su documento, con el certificado bancario ' +
        'adjunto. Lo que se contrasta es que el titular sea el comercio: el número de ' +
        'cuenta y el documento se guardan cifrados y llegan tapados.',
      target: 'admin.bankAccounts.list',
      placement: 'top',
      optionalTarget: true,
    },
    {
      id: 'reject-reason',
      title: 'Rechazar exige motivo, y el comercio lo lee',
      body:
        'Con el motivo, el comercio corrige y vuelve a enviarla. Sin él no sabría qué ' +
        'arreglar y la cuenta daría vueltas. Aprobar y rechazar quedan auditados y se le ' +
        'notifican por la app y por correo.',
    },
    {
      id: 'scope',
      title: 'Qué cuentas ves',
      body:
        'Finanzas ve todas las de su país. Los roles de asesor —soporte, ventas— ven sólo ' +
        'las de las compañías que acompañan: si tu cartera está vacía, la lista sale vacía, ' +
        'y no es un fallo.',
    },
    {
      id: 'registered-elsewhere',
      title: 'La cuenta no se crea desde aquí',
      body:
        'La registra el comercio desde su panel, y para hacerlo necesita un código de un ' +
        'solo uso: no basta con tener la sesión abierta. Si un comercio te pide que le ' +
        'registres tú la cuenta, la respuesta es que la registre él.',
    },
  ],
};
